import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PDT_LINKS } from '@/features/home-v2/constants/links';
import { useChangeApp } from '@/shared/hooks/useChangeApp';
import MagnifyingGlassIcon from '@/assets/icons/home-v2/magnifying-glass.svg?react';
import './QuickSearchInput.css';

/** Number of digits of a valid CPF. */
const CPF_LENGTH = 11;
/** Number of alphanumeric characters of a valid PDT transaction id. */
const TRANSACTION_ID_LENGTH = 32;

export interface QuickSearchInputProps {
  /** Called with the sanitized term after a successful search */
  onSearch?: (value: string) => void;
  /** Custom className */
  className?: string;
}

/**
 * QuickSearchInput
 *
 * Quick lookup of the last 30 days of PDT queries by CPF or transaction id.
 * Figma nodes `1601:13713` (card) and `1601:13741` (input).
 *
 * ## Validation Rules
 * - **CPF**: exactly 11 digits → `PDT_LINKS.CONSULTA_CPF`
 * - **Transaction id**: exactly 32 alphanumeric characters →
 *   `PDT_LINKS.CONSULTA_TRANSACAO`
 * - anything else: the search is inert
 *
 * ## Flow
 *
 * ```mermaid
 * flowchart TD
 *     A[User types] --> B[Sanitize: keep alphanumerics]
 *     B --> C{11 digits?}
 *     C -->|yes| D[CONSULTA_CPF]
 *     C -->|no| E{32 alphanumerics?}
 *     E -->|yes| F[CONSULTA_TRANSACAO]
 *     E -->|no| G[Search disabled]
 *     D --> H[useChangeApp → PDT client]
 *     F --> H
 * ```
 *
 * @example
 * ```tsx
 * <QuickSearchInput onSearch={(term) => track('quick_search', term)} />
 * ```
 *
 * @since 1.0.0
 * @category Molecules
 */
export const QuickSearchInput: React.FC<QuickSearchInputProps> = ({
  onSearch,
  className = '',
}) => {
  const { t } = useTranslation();
  const { mutate: navigateTo } = useChangeApp();
  const [value, setValue] = useState('');

  const term = useMemo(() => value.replace(/[^a-zA-Z0-9]/g, ''), [value]);

  const searchUrl = useMemo(() => {
    if (new RegExp(`^\\d{${CPF_LENGTH}}$`).test(term)) {
      return PDT_LINKS.CONSULTA_CPF(term);
    }
    if (new RegExp(`^[a-zA-Z0-9]{${TRANSACTION_ID_LENGTH}}$`).test(term)) {
      return PDT_LINKS.CONSULTA_TRANSACAO(term);
    }
    return null;
  }, [term]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(
        event.target.value
          .replace(/[^a-zA-Z0-9]/g, '')
          .slice(0, TRANSACTION_ID_LENGTH)
      );
    },
    []
  );

  const handleSearch = useCallback(() => {
    if (!searchUrl) return;

    navigateTo({
      url: searchUrl,
      targetClientId: import.meta.env.VITE_PDT_CLIENT_ID,
    });
    onSearch?.(term);
  }, [searchUrl, navigateTo, onSearch, term]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <div
      className={`quick-search-input ${className}`.trim()}
      role="search"
      aria-label={t('Quick search')}
    >
      <p className="quick-search-input__hint">
        {t('Find queries from the')}{' '}
        <strong className="quick-search-input__hint-strong">
          {t('last 30 days')}
        </strong>
      </p>
      <div className="quick-search-input__field">
        <input
          className="quick-search-input__control"
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('Enter CPF or Transaction ID')}
          aria-label={t('Search by CPF or Transaction ID')}
          maxLength={TRANSACTION_ID_LENGTH}
        />
        <button
          type="button"
          className="quick-search-input__button"
          onClick={handleSearch}
          disabled={!searchUrl}
          aria-label={t('Search')}
        >
          <MagnifyingGlassIcon width={20} height={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default QuickSearchInput;
